import React from "react";
import { useSelector } from "react-redux";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";

import { colors } from "../templates/dark-hero/colors";
import { OVERFLOW_X_CLIP_SX } from "../templates/dark-hero/layoutGuards";
import { PageContainer } from "../templates/dark-hero/PageContainer";
import MatchNightCard from "./MatchNightCard";
import MatchNightRsvpDialog from "./MatchNightRsvpDialog";
import { MATCH_NIGHT_REGIONS, normalizeMatchNightRegion } from "./preferredMatchNight";
import useMatchNights from "./useMatchNights";

const HERO_BG = "#080808";

const SUBHEAD =
  "Everyone shows up at the same time, so games are full and the competition is real. Hand-vetted players, balanced matches, servers in EU and NA.";

const pageRootSx = {
  ...OVERFLOW_X_CLIP_SX,
  minHeight: "100svh",
  backgroundColor: HERO_BG,
  color: colors.text.primary,
};

const sectionSx = {
  py: { xs: "56px", md: "88px" },
  px: { xs: 2, md: 4 },
};

const eyebrowSx = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  mb: 2.5,
};

const eyebrowTextSx = {
  fontSize: "11px",
  fontWeight: 600,
  letterSpacing: "0.14em",
  color: colors.primary.main,
  textTransform: "uppercase",
};

const headlineSx = {
  fontFamily: "'Inter', system-ui, sans-serif",
  fontSize: { xs: "2.25rem", sm: "2.5rem", md: "3.25rem" },
  fontWeight: 700,
  letterSpacing: "-0.04em",
  textTransform: "uppercase",
  lineHeight: 1,
  color: colors.text.primary,
  mb: 2.5,
};

const subheadSx = {
  maxWidth: "38rem",
  fontSize: { xs: "0.9rem", md: "1rem" },
  lineHeight: 1.7,
  color: colors.text.secondary,
  mb: 3,
};

const tabRowSx = {
  display: "inline-flex",
  gap: 0.5,
  mb: { xs: 5, md: 7 },
  p: 0.35,
  border: `1px solid ${colors.border.default}`,
  borderRadius: "999px",
  backgroundColor: "rgba(0,0,0,0.22)",
};

function tabSx(active) {
  return {
    border: 0,
    borderRadius: "999px",
    px: 1.4,
    py: 0.55,
    cursor: "pointer",
    fontFamily: "'Inter', system-ui, sans-serif",
    fontSize: "0.75rem",
    fontWeight: 700,
    color: active ? "#0B0D0F" : colors.text.secondary,
    backgroundColor: active ? colors.primary.main : "transparent",
    "&:hover": {
      color: active ? "#0B0D0F" : colors.text.primary,
      backgroundColor: active ? colors.primary.main : "rgba(255,255,255,0.06)",
    },
  };
}

const gridSx = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
  gap: { xs: "16px", md: "24px" },
};

const emptyStateSx = {
  color: colors.text.muted,
  fontSize: "0.95rem",
  py: 6,
};

function selectUserCountry(state) {
  return state?.catalogReducer?.user?.country || state?.catalog?.user?.country || null;
}

export default function MatchNightPage() {
  const country = useSelector(selectUserCountry);
  const { nights, preferredRegion, loading, basePath, refetch } = useMatchNights({ country });
  const [selectedNight, setSelectedNight] = React.useState(null);
  const [selectedRegion, setSelectedRegion] = React.useState(() =>
    normalizeMatchNightRegion(preferredRegion)
  );
  const hasAppliedPreferredRegion = React.useRef(false);

  React.useEffect(() => {
    if (hasAppliedPreferredRegion.current) return;
    if (!MATCH_NIGHT_REGIONS.includes(preferredRegion)) return;
    setSelectedRegion(preferredRegion);
    hasAppliedPreferredRegion.current = true;
  }, [preferredRegion]);

  const visibleNights = React.useMemo(
    () => nights.filter((night) => night.region === selectedRegion),
    [nights, selectedRegion]
  );

  return (
    <Box sx={pageRootSx}>
      <Box component="section" sx={sectionSx}>
        <PageContainer sx={{ maxWidth: "68rem" }}>
          <Box sx={eyebrowSx}>
            <Box sx={{ width: 24, height: "1px", background: colors.primary.hover }} />
            <Typography sx={eyebrowTextSx}>A new era of cs2 matchmaking</Typography>
          </Box>
          <Typography component="h1" sx={headlineSx}>
            Match night.
          </Typography>
          <Typography sx={subheadSx}>{SUBHEAD}</Typography>

          <Box role="tablist" aria-label="Match night region" sx={tabRowSx}>
            {MATCH_NIGHT_REGIONS.map((region) => (
              <Box
                key={region}
                component="button"
                type="button"
                role="tab"
                aria-selected={selectedRegion === region}
                onClick={() => setSelectedRegion(region)}
                sx={tabSx(selectedRegion === region)}
              >
                {region}
              </Box>
            ))}
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress sx={{ color: colors.primary.main }} />
            </Box>
          ) : visibleNights.length === 0 ? (
            <Typography sx={emptyStateSx}>
              No {selectedRegion} match nights are open for reservations right now. Check back soon.
            </Typography>
          ) : (
            <Box sx={gridSx}>
              {visibleNights.map((night) => (
                <MatchNightCard key={night.id} night={night} onReserve={setSelectedNight} />
              ))}
            </Box>
          )}
        </PageContainer>
      </Box>

      <MatchNightRsvpDialog
        open={Boolean(selectedNight)}
        night={selectedNight}
        basePath={basePath}
        onClose={() => setSelectedNight(null)}
        onRsvpSuccess={refetch}
      />
    </Box>
  );
}
